import { useReducer, useCallback, useEffect } from 'react';
import { HeroState, HeroAction, DemoResponse, CTACard } from '../model/DemoModel';
import { heroApi } from '../api/demoApi';

const initialHeroContent = {
  title: 'AI-Powered Talent',
  subtitle: 'Matching Platform',
  description: 'Connect top talent with leading companies through intelligent matching and comprehensive recruitment tools'
};

const initialState: HeroState = {
  companyDemo: null,
  jobSeekerDemo: null,
  heroContent: initialHeroContent,
  ctaCards: [],
  loading: false,
  error: null,
  isDemoModalOpen: false,
  selectedDemoType: null,
  selectedDemoUrl: null
};

const heroReducer = (state: HeroState, action: HeroAction): HeroState => {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        loading: true,
        error: null
      };

    case 'FETCH_SUCCESS':
      // Create CTACards dynamically from API response
      const ctaCards: CTACard[] = [
        {
          type: 'company',
          title: action.payload.company.title,
          description: action.payload.company.description,
          buttonText: 'Start Free Trial',
          demoButtonText: 'Watch Demo',
          buttonLink: '/auth/company-signup',
          videoUrl: action.payload.company.video,
          thumbnailUrl: action.payload.company.thumbnail
        },
        {
          type: 'jobseeker',
          title: action.payload.jobseeker.title,
          description: action.payload.jobseeker.description,
          buttonText: 'Create Profile - Free to Start',
          demoButtonText: 'Watch Demo',
          badgeText: 'Free profile creation & basic features',
          buttonLink: '/auth/candidate-signup',
          videoUrl: action.payload.jobseeker.video,
          thumbnailUrl: action.payload.jobseeker.thumbnail
        }
      ];

      return {
        ...state,
        companyDemo: action.payload.company,
        jobSeekerDemo: action.payload.jobseeker,
        ctaCards,
        loading: false,
        error: null
      };

    case 'FETCH_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    case 'OPEN_DEMO_MODAL':
      return {
        ...state,
        isDemoModalOpen: true,
        selectedDemoType: action.payload.type,
        selectedDemoUrl: action.payload.url
      };

    case 'CLOSE_DEMO_MODAL':
      return {
        ...state,
        isDemoModalOpen: false,
        selectedDemoType: null,
        selectedDemoUrl: null
      };

    default:
      return state;
  }
};

export const useHeroViewModel = () => {
  const [state, dispatch] = useReducer(heroReducer, initialState);

  const fetchDemos = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });
    
    try {
      const demos = await heroApi.getDemos();
      dispatch({ type: 'FETCH_SUCCESS', payload: demos });
    } catch (error) {
      dispatch({ 
        type: 'FETCH_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to load demos' 
      });
    }
  }, []);

  const handleCompanyDemo = useCallback(() => {
    if (state.companyDemo?.video) {
      dispatch({ 
        type: 'OPEN_DEMO_MODAL', 
        payload: { type: 'company', url: state.companyDemo.video } 
      });
    }
  }, [state.companyDemo]);

  const handleJobSeekerDemo = useCallback(() => {
    if (state.jobSeekerDemo?.video) {
      dispatch({ 
        type: 'OPEN_DEMO_MODAL', 
        payload: { type: 'jobseeker', url: state.jobSeekerDemo.video } 
      });
    }
  }, [state.jobSeekerDemo]);

  const closeDemoModal = useCallback(() => {
    dispatch({ type: 'CLOSE_DEMO_MODAL' });
  }, []);

  useEffect(() => {
    fetchDemos();
  }, [fetchDemos]);

  return {
    // State
    companyDemo: state.companyDemo,
    jobSeekerDemo: state.jobSeekerDemo,
    heroContent: state.heroContent,
    loading: state.loading,
    error: state.error,
    isDemoModalOpen: state.isDemoModalOpen,
    selectedDemoType: state.selectedDemoType,
    selectedDemoUrl: state.selectedDemoUrl,

    // Cards - derived from state
    companyCard: state.ctaCards.find(card => card.type === 'company'),
    jobSeekerCard: state.ctaCards.find(card => card.type === 'jobseeker'),

    // Actions
    fetchDemos,
    handleCompanyDemo,
    handleJobSeekerDemo,
    closeDemoModal,

    // Computed
    isLoading: state.loading,
    hasError: !!state.error,
    
    // Helper
    hasData: state.ctaCards.length > 0
  };
};